import * as React from 'react';
import { FC, useCallback, useState } from 'react';

import { NumberInput } from 'components/ucl/number-input';
import { selectors, useAppSelector } from 'state/global-state';

import { useIncrementReward } from '../../loyalty-rewards/use-increment-reward';
import { useRemoveReward } from '../../loyalty-rewards/use-remove-reward';

import {
  incrementorDisplayValueStyleProps as displayValueStyleProps,
  incrementorButtonStyleProps as incrementorStyleProps,
} from './incrementor.styled';

interface IIncentiveCardIncrementorProps {
  /** Sanity reward document id. */
  rewardId: string;
  /** User-level cap on total reward applications (`LoyaltyUser.rewardRedemptionLimit`). */
  maxQuantity: number;
}

/**
 * Quantity control rendered for an applied reward when `rewardRedemptionLimit > 1`.
 * Increment clones the existing cart entry and dispatches `applyReward` — the same two
 * steps the PDP add-to-cart button performs — without navigating back through the wizard.
 * Decrementing to zero removes the reward entirely and transitions the card back to
 * the add-button state.
 * Increment is disabled when the total applied count across ALL rewards reaches the
 * user-level cap (`rewardRedemptionLimit`).
 */
export const IncentiveCardIncrementor: FC<IIncentiveCardIncrementorProps> = ({
  rewardId,
  maxQuantity,
}) => {
  const appliedRewardsArray = useAppSelector(selectors.loyalty.selectAppliedLoyaltyRewardsArray);

  // Fall back to 1 rather than 0 when Redux is not yet hydrated: `useAppliedRewards` may
  // have set `isApplied` via the minimal-cart fallback (which carries no timesApplied), so
  // the selector returns null here. Defaulting to 0 would disable the decrement button and
  // leave the user with no way to remove an applied reward from this control.
  const currentQuantity =
    appliedRewardsArray
      ?.filter(r => r.sanityId === rewardId)
      .reduce((sum, r) => sum + (r.timesApplied ?? 0), 0) ?? 1;

  const totalApplied = appliedRewardsArray?.reduce((sum, r) => sum + (r.timesApplied ?? 0), 0) ?? 0;

  const [isIncrementing, setIsIncrementing] = useState(false);

  const { handleRemove } = useRemoveReward(rewardId);
  const { handleIncrement } = useIncrementReward(rewardId);

  const handleChange = useCallback(
    async (newValue: number) => {
      if (newValue > currentQuantity) {
        setIsIncrementing(true);
        try {
          await handleIncrement();
        } finally {
          setIsIncrementing(false);
        }
      } else {
        handleRemove();
      }
    },
    [currentQuantity, handleIncrement, handleRemove]
  );

  return (
    <NumberInput
      ariaLabel={`${currentQuantity}`}
      min={0}
      max={maxQuantity}
      value={currentQuantity}
      onChange={handleChange}
      disabled={isIncrementing}
      disableIncrement={totalApplied >= maxQuantity}
      decrementAriaLabel={`Decrement ${currentQuantity} quantity`}
      incrementAriaLabel={`Increment ${currentQuantity} quantity`}
      displayValueStyleProps={displayValueStyleProps}
      incrementorStyleProps={incrementorStyleProps}
      hideNumberInput
      displayedValue={currentQuantity}
    />
  );
};
